import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ExtractionRun,
  SnbDocumentKind,
  StagedExtractionResult,
  TombstoneDraft,
  TombstoneOverride,
  UpdateFileDto,
} from '@munifocus/contracts-gov';
import { api } from './api-client';
import { filesKeys } from './files-api';

/**
 * Stage 1 upload-and-extract.
 *
 * Two upload paths, because a file may or may not exist yet: the intake form
 * extracts before creation and passes the returned `stagingId` through to
 * `POST /files`, while the tombstone editor extracts against an existing file.
 */

export async function extractStaged(
  document: File,
  kind: SnbDocumentKind,
  /** Staged uploads already made on this form, so the draft merges all of them. */
  priorStagingIds: string[] = [],
): Promise<StagedExtractionResult> {
  // Let the browser set the multipart boundary — don't set Content-Type by hand.
  const form = new FormData();
  form.append('document', document);
  form.append('kind', kind);
  // Repeated field: the DTO normalises a lone value back into an array.
  for (const id of priorStagingIds) form.append('priorStagingIds', id);
  const { data } = await api.post<StagedExtractionResult>('/property-extraction/staged', form);
  return data;
}

export async function extractForFile(
  fileId: string,
  document: File,
  kind: SnbDocumentKind,
): Promise<ExtractionRun> {
  const form = new FormData();
  form.append('document', document);
  form.append('kind', kind);
  const { data } = await api.post<ExtractionRun>(`/files/${fileId}/property-extraction`, form);
  return data;
}

export async function listExtractions(fileId: string): Promise<ExtractionRun[]> {
  const { data } = await api.get<ExtractionRun[]>(`/files/${fileId}/property-extraction`);
  return data;
}

export async function getDraft(fileId: string): Promise<TombstoneDraft> {
  const { data } = await api.get<TombstoneDraft>(`/files/${fileId}/property-extraction/draft`);
  return data;
}

export async function listOverrides(fileId: string): Promise<TombstoneOverride[]> {
  const { data } = await api.get<TombstoneOverride[]>(
    `/files/${fileId}/property-extraction/overrides`,
  );
  return data;
}

export async function applyExtraction(
  fileId: string,
  patch: UpdateFileDto,
): Promise<{ overrides: TombstoneOverride[] }> {
  const { data } = await api.post<{ overrides: TombstoneOverride[] }>(
    `/files/${fileId}/property-extraction/apply`,
    patch,
  );
  return data;
}

export const extractionKeys = {
  runs: (fileId: string) => ['property-extraction', fileId] as const,
  draft: (fileId: string) => ['property-extraction', fileId, 'draft'] as const,
  overrides: (fileId: string) => ['property-extraction', fileId, 'overrides'] as const,
};

export function useExtractions(fileId: string | undefined) {
  return useQuery({
    queryKey: extractionKeys.runs(fileId ?? ''),
    queryFn: () => listExtractions(fileId as string),
    enabled: !!fileId,
  });
}

export function useExtractionDraft(fileId: string | undefined) {
  return useQuery({
    queryKey: extractionKeys.draft(fileId ?? ''),
    queryFn: () => getDraft(fileId as string),
    enabled: !!fileId,
  });
}

export function useTombstoneOverrides(fileId: string | undefined) {
  return useQuery({
    queryKey: extractionKeys.overrides(fileId ?? ''),
    queryFn: () => listOverrides(fileId as string),
    enabled: !!fileId,
  });
}

/** Upload and extract against an existing file, then refresh the draft. */
export function useExtractForFile(fileId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ document, kind }: { document: File; kind: SnbDocumentKind }) =>
      extractForFile(fileId as string, document, kind),
    onSuccess: () => {
      if (!fileId) return;
      qc.invalidateQueries({ queryKey: extractionKeys.runs(fileId) });
      qc.invalidateQueries({ queryKey: extractionKeys.draft(fileId) });
    },
  });
}

/**
 * Apply reviewed values. Invalidates the file itself as well as the extraction
 * queries — applying patches the tombstone, so the file detail is now stale.
 */
export function useApplyExtraction(fileId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: UpdateFileDto) => applyExtraction(fileId as string, patch),
    onSuccess: () => {
      if (!fileId) return;
      qc.invalidateQueries({ queryKey: filesKeys.detail(fileId) });
      qc.invalidateQueries({ queryKey: extractionKeys.overrides(fileId) });
    },
  });
}
